import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, Container, Row, Col, Button, Spinner, Alert, Badge, Form, Modal } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { getPup, reactToPup, updatePup, deletePup } from '../services/pupService';
import { getBreeds } from '../services/breedService';
import { Pup, Reaction, Breed } from '../types/breed';
import Select from 'react-select';

// Define interface for breed select options
interface BreedOption {
  value: string;
  label: string;
  isRare?: boolean;
}

const PupDetailPage: React.FC = () => {
  const { pupId } = useParams<{ pupId: string }>();
  const [pup, setPup] = useState<Pup | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [reactLoading, setReactLoading] = useState<boolean>(false);
  const [userReactions, setUserReactions] = useState<Set<string>>(new Set());
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  
  // Edit mode state
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [editLoading, setEditLoading] = useState<boolean>(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [dogName, setDogName] = useState<string>('');
  const [caption, setCaption] = useState<string>('');
  const [locationName, setLocationName] = useState<string>('');
  const [selectedBreedOption, setSelectedBreedOption] = useState<BreedOption | null>(null);
  const [breedOptions, setBreedOptions] = useState<BreedOption[]>([]);
  const [breeds, setBreeds] = useState<Breed[]>([]);
  
  // Delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Fetch pup data
  useEffect(() => {
    const fetchPup = async () => {
      if (!pupId) return;
      
      try {
        setLoading(true);
        setError(null);
        const pupData = await getPup(parseInt(pupId));
        setPup(pupData);

        // Set user reactions from the backend response
        if (pupData.user_reactions && pupData.user_reactions.length > 0) {
          setUserReactions(new Set(pupData.user_reactions));
        }
        
        // Initialize edit form fields from pup data
        setDogName(pupData.dog_name || '');
        setCaption(pupData.caption || '');
        setLocationName(pupData.location_name || '');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load pup details');
      } finally {
        setLoading(false);
      }
    };

    fetchPup();
  }, [pupId]);
  
  // Fetch breed data for edit form
  useEffect(() => {
    const fetchBreeds = async () => {
      try {
        const breedsData = await getBreeds();
        setBreeds(breedsData);
        
        // Create options for the select dropdown
        const options = breedsData.map(breed => ({
          value: breed.id.toString(),
          label: breed.name,
          isRare: breed.is_rare
        }));
        setBreedOptions(options);
        
        // Set the currently selected breed if pup data is available
        if (pup && pup.breed_id) {
          const currentBreed = options.find(option => 
            parseInt(option.value) === pup.breed_id
          );
          if (currentBreed) {
            setSelectedBreedOption(currentBreed);
          }
        }
      } catch (err) {
        console.error('Failed to load breeds:', err);
      }
    };
    
    if (showEditModal) {
      fetchBreeds();
    }
  }, [showEditModal, pup]);

  // Handle reaction
  const handleReaction = async (reactionType: Reaction['reaction_type']) => {
    if (!pupId || !isAuthenticated) return;
    
    try {
      setReactLoading(true);
      const response = await reactToPup(parseInt(pupId), reactionType);
      
      // Refresh pup data to get updated reactions
      const updatedPup = await getPup(parseInt(pupId));
      setPup(updatedPup);
      
      // Update user reactions locally
      setUserReactions(prevReactions => {
        const newReactions = new Set(prevReactions);
        if (response.action === 'added') {
          newReactions.add(reactionType);
        } else if (response.action === 'removed') {
          newReactions.delete(reactionType);
        }
        return newReactions;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add reaction');
    } finally {
      setReactLoading(false);
    }
  };
  
  // Handle edit form submission
  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!pupId) return;
    
    try {
      setEditLoading(true);
      setEditError(null);
      
      // Create update data object
      const updateData: {
        breed_id?: number;
        dog_name?: string;
        caption?: string;
        location_name?: string;
      } = {};
      
      // Only include changed fields
      if (selectedBreedOption) {
        updateData.breed_id = parseInt(selectedBreedOption.value);
      }
      
      updateData.dog_name = dogName;
      updateData.caption = caption;
      updateData.location_name = locationName;
      
      // Update the pup
      const updatedPup = await updatePup(parseInt(pupId), updateData);
      
      // Update local state with new data
      setPup(updatedPup);
      
      // Close the edit modal
      setShowEditModal(false);
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Failed to update pup');
    } finally {
      setEditLoading(false);
    }
  };
  
  // Handle edit button click
  const handleEditClick = () => {
    if (pup) {
      setDogName(pup.dog_name || '');
      setCaption(pup.caption || '');
      setLocationName(pup.location_name || '');
      
      // Find the current breed in the options
      if (breedOptions.length > 0) {
        const currentBreed = breedOptions.find(option => 
          parseInt(option.value) === pup.breed_id
        );
        if (currentBreed) {
          setSelectedBreedOption(currentBreed);
        }
      }
      
      setShowEditModal(true);
    }
  };

  // Handle delete button click
  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };
  
  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!pupId) return;
    
    try {
      setDeleteLoading(true);
      setDeleteError(null);
      
      // Delete the pup
      await deletePup(parseInt(pupId));
      
      // Close modals
      setShowDeleteModal(false);
      setShowEditModal(false);
      
      // Navigate back to home page
      navigate('/');
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete pup');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Format the created_at date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };
  
  // Check if user is authorized to edit
  const canEdit = () => {
    if (!isAuthenticated || !user || !pup) return false;
    return user.is_admin || pup.user_id === user.id;
  };

  if (loading) {
    return (
      <Container className="text-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="my-4">
        <Alert variant="danger">
          {error}
        </Alert>
        <Button variant="primary" onClick={() => navigate('/')}>
          Back to Home
        </Button>
      </Container>
    );
  }

  if (!pup) {
    return (
      <Container className="my-4">
        <Alert variant="warning">
          This pup could not be found.
        </Alert>
        <Button variant="primary" onClick={() => navigate('/')}>
          Back to Home
        </Button>
      </Container>
    );
  }

  // Get reaction counts
  const reactionCounts = pup.reactions || {};

  return (
    <Container className="my-4">
      <Row>
        <Col lg={9} className="mb-4">
          <div className="position-relative mb-4">
            <div className="pup-image-container bg-light rounded shadow-sm p-3 mb-4 text-center">
              <img
                src={pup.image_url}
                alt={`${pup.dog_name || 'A'} ${pup.breed}`}
                className="img-fluid rounded"
                style={{ 
                  maxWidth: '100%',
                  maxHeight: '600px',
                  objectFit: 'contain'
                }}
              />
              {pup.is_rare && (
                <div 
                  className="position-absolute top-0 end-0 m-3 badge bg-warning text-dark p-2"
                  style={{ fontSize: '1rem' }}
                >
                  <i className="bi bi-star-fill me-1"></i> Rare Breed
                </div>
              )}
            </div>
            
            <Card className="border-0 shadow-sm mb-4">
              <Card.Body>
                <Card.Title className="mb-3 d-flex justify-content-between align-items-center">
                  <h3 className="mb-0">{pup.dog_name || `A ${pup.breed}`}</h3>
                  <div className="d-flex align-items-center">
                    {canEdit() && (
                      <Button 
                        variant="outline-primary" 
                        size="sm" 
                        className="me-2"
                        onClick={handleEditClick}
                      >
                        <i className="bi bi-pencil me-1"></i> Edit
                      </Button>
                    )}
                    <Link to={`/users/${pup.username}`} className="text-decoration-none">
                      <small className="text-muted d-flex align-items-center">
                        <i className="bi bi-person me-1"></i> {pup.username}
                      </small>
                    </Link>
                  </div>
                </Card.Title>
                
                {pup.caption && (
                  <Card.Text className="mb-3 lead">
                    {pup.caption}
                  </Card.Text>
                )}
                
                <hr className="my-3" />
                
                <div className="d-flex justify-content-between align-items-center text-muted small">
                  <div>
                    {pup.location_name && (
                      <span className="me-3">
                        <i className="bi bi-geo-alt me-1"></i> {pup.location_name}
                      </span>
                    )}
                    <span>
                      <i className="bi bi-calendar3 me-1"></i> {formatDate(pup.created_at)}
                    </span>
                  </div>
                  <Link to={`/breeds/${pup.breed_id}`} className="text-decoration-none text-muted">
                    <i className="bi bi-tag me-1"></i> {pup.breed}
                  </Link>
                </div>
              </Card.Body>
            </Card>
          </div>
        </Col>
        
        <Col lg={3}>
          <Card className="mb-4 border-0 shadow-sm">
            <Card.Header className="bg-white">
              <strong>Reactions</strong>
            </Card.Header>
            <Card.Body>
              {isAuthenticated ? (
                <div className="d-grid gap-2">
                  <Button 
                    variant={userReactions.has('cute') ? "danger" : "outline-danger"}
                    className={userReactions.has('cute') ? "" : "border"}
                    disabled={reactLoading}
                    onClick={() => handleReaction('cute')}
                    size="lg"
                  >
                    <span className="d-flex justify-content-between align-items-center">
                      <span>😍 Cute</span>
                      {reactionCounts.cute ? <Badge bg="light" text="dark" pill>{reactionCounts.cute}</Badge> : ''}
                    </span>
                  </Button>
                  <Button
                    variant={userReactions.has('want_it') ? "warning" : "outline-warning"}
                    className={userReactions.has('want_it') ? "" : "border"}
                    disabled={reactLoading}
                    onClick={() => handleReaction('want_it')}
                    size="lg"
                  >
                    <span className="d-flex justify-content-between align-items-center">
                      <span>🦴 Good boy</span>
                      {reactionCounts.want_it ? <Badge bg="light" text="dark" pill>{reactionCounts.want_it}</Badge> : ''}
                    </span>
                  </Button>
                  <Button 
                    variant={userReactions.has('fluffy') ? "primary" : "outline-primary"}
                    className={userReactions.has('fluffy') ? "" : "border"}
                    disabled={reactLoading}
                    onClick={() => handleReaction('fluffy')}
                    size="lg"
                  >
                    <span className="d-flex justify-content-between align-items-center">
                      <span>🧸 Fluffy</span>
                      {reactionCounts.fluffy ? <Badge bg="light" text="dark" pill>{reactionCounts.fluffy}</Badge> : ''}
                    </span>
                  </Button>
                  <Button
                    variant={userReactions.has('sad') ? "secondary" : "outline-secondary"}
                    className={userReactions.has('sad') ? "" : "border"}
                    disabled={reactLoading}
                    onClick={() => handleReaction('sad')}
                    size="lg"
                  >
                    <span className="d-flex justify-content-between align-items-center">
                      <span>🥺 Aww</span>
                      {reactionCounts.sad ? <Badge bg="light" text="dark" pill>{reactionCounts.sad}</Badge> : ''}
                    </span>
                  </Button>
                </div>
              ) : (
                <>
                  <div className="text-center py-3">
                    <div className="mb-3">
                      <span className="display-6">👋</span>
                    </div>
                    <p className="mb-3">Sign in to react to this pup!</p>
                    <div className="d-grid">
                      <Button variant="primary" onClick={() => navigate('/login')}>
                        Sign In
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
          
          <Card className="mb-4 border-0 shadow-sm">
            <Card.Header className="bg-white">
              <strong>About This Pup</strong>
            </Card.Header>
            <Card.Body>
              <dl className="row mb-0">
                <dt className="col-sm-4">Breed</dt>
                <dd className="col-sm-8">
                  <Link to={`/breeds/${pup.breed_id}`} className="text-decoration-none">
                    {pup.breed} 
                    <i className="bi bi-box-arrow-up-right small ms-1 text-muted"></i>
                  </Link>
                </dd>
                
                {pup.dog_name && (
                  <>
                    <dt className="col-sm-4">Name</dt>
                    <dd className="col-sm-8">{pup.dog_name}</dd>
                  </>
                )}
                
                <dt className="col-sm-4">Posted By</dt>
                <dd className="col-sm-8">
                  <Link to={`/users/${pup.username}`} className="text-decoration-none">
                    {pup.username}
                    <i className="bi bi-box-arrow-up-right small ms-1 text-muted"></i>
                  </Link>
                </dd>
                
                {pup.location_name && (
                  <>
                    <dt className="col-sm-4">Location</dt>
                    <dd className="col-sm-8">
                      <i className="bi bi-geo-alt me-1"></i>
                      {pup.location_name}
                    </dd>
                  </>
                )}
                
                <dt className="col-sm-4">Posted On</dt>
                <dd className="col-sm-8">
                  <i className="bi bi-calendar3 me-1"></i>
                  {formatDate(pup.created_at)}
                </dd>
                
                {pup.is_rare && (
                  <>
                    <dt className="col-sm-4">Status</dt>
                    <dd className="col-sm-8">
                      <Badge bg="warning" className="text-dark">Rare Breed</Badge>
                    </dd>
                  </>
                )}
              </dl>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      <div className="text-center mt-4 mb-4">
        <Button variant="outline-secondary" onClick={() => navigate('/')} className="px-4">
          <i className="bi bi-arrow-left me-2"></i>Back to All Pups
        </Button>
      </div>
      
      {/* Edit Pup Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Pup</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editError && (
            <Alert variant="danger" className="mb-3">
              {editError}
            </Alert>
          )}
          
          <Form onSubmit={handleEditSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Breed</Form.Label>
              <Select
                options={breedOptions}
                value={selectedBreedOption}
                onChange={(option) => setSelectedBreedOption(option as BreedOption)}
                isSearchable
                placeholder="Select breed..."
                formatOptionLabel={(option: BreedOption) => (
                  <div>
                    {option.label}
                    {option.isRare && (
                      <Badge bg="warning" text="dark" className="ms-2">Rare</Badge>
                    )}
                  </div>
                )}
                classNamePrefix="breed-select"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Dog Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="What's this pup's name?"
                value={dogName}
                onChange={(e) => setDogName(e.target.value)}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Caption</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Say something about this pup..."
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Location</Form.Label>
              <Form.Control
                type="text"
                placeholder="Where was this photo taken?"
                value={locationName}
                onChange={(e) => setLocationName(e.target.value)}
              />
            </Form.Group>
            
            <div className="d-flex justify-content-between">
              <Button 
                variant="danger" 
                onClick={handleDeleteClick}
                disabled={editLoading || deleteLoading}
              >
                <i className="bi bi-trash me-1"></i> Delete
              </Button>
              
              <div>
                <Button 
                  variant="secondary" 
                  className="me-2"
                  onClick={() => setShowEditModal(false)}
                  disabled={editLoading || deleteLoading}
                >
                  Cancel
                </Button>
                <Button 
                  variant="primary" 
                  type="submit"
                  disabled={editLoading || deleteLoading}
                >
                  {editLoading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Saving...
                    </>
                  ) : 'Save Changes'}
                </Button>
              </div>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
      
      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteError && (
            <Alert variant="danger" className="mb-3">
              {deleteError}
            </Alert>
          )}
          
          <p>Are you sure you want to delete this pup? This action cannot be undone.</p>
          
          <div className="d-flex justify-content-end mt-3">
            <Button 
              variant="secondary" 
              className="me-2"
              onClick={() => setShowDeleteModal(false)}
              disabled={deleteLoading}
            >
              Cancel
            </Button>
            <Button 
              variant="danger" 
              onClick={handleDeleteConfirm}
              disabled={deleteLoading}
            >
              {deleteLoading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Deleting...
                </>
              ) : 'Delete Pup'}
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default PupDetailPage;